import React, { createContext, useContext, useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { useAuthStore } from './stores/authStore';

// PostHog configuration
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com';
const IS_PRODUCTION = import.meta.env.MODE === 'production';
const ANALYTICS_ENABLED = import.meta.env.VITE_ANALYTICS_ENABLED !== 'false';

// Initialize PostHog only if we have a key and analytics are enabled
let posthogInitialized = false;

const initPostHog = () => {
  // Debug logging
  if (import.meta.env.DEV) {
    console.log('[PostHog] Initialization check:', {
      posthogInitialized,
      hasKey: !!POSTHOG_KEY,
      keyLength: POSTHOG_KEY?.length || 0,
      analyticsEnabled: ANALYTICS_ENABLED,
      isProduction: IS_PRODUCTION,
      debugMode: import.meta.env.VITE_POSTHOG_DEBUG === 'true',
    });
  }

  if (posthogInitialized) {
    if (import.meta.env.DEV) {
      console.log('[PostHog] Already initialized');
    }
    return false;
  }

  if (!POSTHOG_KEY) {
    if (import.meta.env.DEV) {
      console.warn('[PostHog] Missing VITE_POSTHOG_KEY environment variable');
    }
    return false;
  }

  if (!ANALYTICS_ENABLED) {
    if (import.meta.env.DEV) {
      console.log('[PostHog] Analytics disabled via VITE_ANALYTICS_ENABLED');
    }
    return false;
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: false,
      capture_pageleave: true,
      respect_dnt: true,
      loaded: (posthog) => {
        if (typeof window !== 'undefined') {
          (window as any).posthog = posthog;
        }
        
        if (!IS_PRODUCTION && import.meta.env.VITE_POSTHOG_DEBUG !== 'true') {
          posthog.opt_out_capturing();
          console.log('[PostHog] Disabled in development mode (set VITE_POSTHOG_DEBUG=true to enable)');
        } else {
          console.log('[PostHog] Initialized successfully');
          if (import.meta.env.DEV) {
            console.log('[PostHog] Available at window.posthog');
          }
        }
      },
      // Session recording settings (disabled by default for privacy)
      disable_session_recording: true,
      // Autocapture settings
      autocapture: {
        dom_event_allowlist: ['click', 'submit'],
        element_allowlist: ['button', 'a', 'form'],
        css_selector_allowlist: ['[data-track]'],
      },
      // Persistence settings
      persistence: 'localStorage+cookie',
    });

    // Also attach immediately (in case loaded callback hasn't fired yet)
    if (typeof window !== 'undefined') {
      (window as any).posthog = posthog;
    }

    posthogInitialized = true;
    return true;
  } catch (error) {
    console.error('[PostHog] Initialization failed:', error);
    return false;
  }
};

// Analytics event types for type safety
export interface AnalyticsEvents {
  // Page events
  page_viewed: { path: string; title?: string };

  // Auth events
  user_signed_in: { method: string };
  user_signed_out: Record<string, never>;

  // Workout events
  workout_generated: { exercise_count: number; source: string };
  workout_started: { workout_id: string; workout_type: string };
  workout_completed: { workout_id: string; duration_seconds: number };
  workout_abandoned: { workout_id: string; completion_percentage: number };
  exercise_completed: { exercise_id: string; exercise_name: string };
  set_completed: { exercise_id: string; weight?: number; reps?: number };

  // Beta application
  beta_application_submitted: { training_frequency: string; has_goal: boolean };

  // Navigation
  navigation_clicked: { destination: string; source?: string };

  // Errors
  error_occurred: { error_type: string; message: string; context?: string };
}

// Type-safe analytics tracking interface
interface AnalyticsContextType {
  track: <T extends keyof AnalyticsEvents>(
    event: T,
    properties?: AnalyticsEvents[T]
  ) => void;
  pageView: (path: string, title?: string) => void;
  identify: (userId: string, traits?: Record<string, any>) => void;
  reset: () => void;
  isEnabled: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType | null>(null);

// PostHog Provider component
export const PostHogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated } = useAuthStore();
  const previousPathRef = useRef<string>('');

  // Initialize PostHog on mount
  useEffect(() => {
    initPostHog();
  }, []);

  // Identify user when they sign in
  useEffect(() => {
    if (!posthogInitialized || !POSTHOG_KEY) return;

    if (isAuthenticated && user) {
      posthog.identify(user.id, {
        email: user.email,
        name: user.name,
        created_at: user.created_at,
      });
      console.log('[PostHog] User identified:', user.id);
    }
  }, [isAuthenticated, user]);

  // Reset on logout
  useEffect(() => {
    if (!posthogInitialized || !POSTHOG_KEY) return;

    if (!isAuthenticated) {
      posthog.reset();
      console.log('[PostHog] User reset');
    }
  }, [isAuthenticated]);

  // Track function with type safety
  const track = <T extends keyof AnalyticsEvents>(
    event: T,
    properties?: AnalyticsEvents[T]
  ) => {
    if (!posthogInitialized || !POSTHOG_KEY || !ANALYTICS_ENABLED) {
      if (import.meta.env.DEV) {
        console.log('[PostHog] Track (disabled):', event, properties);
      }
      return;
    }

    posthog.capture(event, properties);

    if (import.meta.env.DEV && import.meta.env.VITE_POSTHOG_DEBUG === 'true') {
      console.log('[PostHog] Track:', event, properties);
    }
  };

  // Page view tracking
  const pageView = (path: string, title?: string) => {
    if (!posthogInitialized || !POSTHOG_KEY || !ANALYTICS_ENABLED) {
      if (import.meta.env.DEV) {
        console.log('[PostHog] Page view (disabled):', path);
      }
      return;
    }

    // Avoid duplicate pageviews
    if (previousPathRef.current === path) return;
    previousPathRef.current = path;

    posthog.capture('$pageview', {
      $current_url: window.location.href,
      path,
      title: title || document.title,
    });

    if (import.meta.env.DEV && import.meta.env.VITE_POSTHOG_DEBUG === 'true') {
      console.log('[PostHog] Page view:', path);
    }
  };

  // Identify user
  const identify = (userId: string, traits?: Record<string, any>) => {
    if (!posthogInitialized || !POSTHOG_KEY || !ANALYTICS_ENABLED) return;
    posthog.identify(userId, traits);
  };

  // Reset user identity (on logout)
  const reset = () => {
    if (!posthogInitialized || !POSTHOG_KEY) return;
    posthog.reset();
  };

  const contextValue: AnalyticsContextType = {
    track,
    pageView,
    identify,
    reset,
    isEnabled: posthogInitialized && ANALYTICS_ENABLED,
  };

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Hook to use analytics
export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);

  // Return a no-op implementation if used outside provider
  if (!context) {
    return {
      track: () => { },
      pageView: () => { },
      identify: () => { },
      reset: () => { },
      isEnabled: false,
    };
  }

  return context;
};

// Hook for page view tracking with router
export const usePageView = (path: string, title?: string) => {
  const { pageView } = useAnalytics();

  useEffect(() => {
    pageView(path, title);
  }, [path, title, pageView]);
};

// Export posthog instance for advanced usage
export { posthog };
